import { CardGiftcardOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useRef, useState } from "react";
import ProductCard from "@/components/products/ProductCard";
import { useIsMobile } from "@/hooks/useMediaQuery";
import type { Product } from "@/interfaces/product";
import WidgedHeader from "./WidgedHeader";
import WidgetEmptyState from "./WidgetEmptyState";

/** Desktop: ~4 cards visible at 20% width; one dot per page */
const DESKTOP_CARDS_PER_PAGE = 4;
/** Must match the card wrapper maxWidth in the JSX so scroll math matches layout */
const CARD_MAX_WIDTH_PX = 265;

type GiftCarouselProps = {
  gifts: Product[];
};

const GiftCarousel = ({ gifts }: GiftCarouselProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const isMobile = useIsMobile();

  // Empty state — no top gifts to show yet. Returned after hooks to respect rules of hooks.
  if (gifts.length === 0) {
    return (
      <Box sx={{ width: "100%" }}>
        <WidgedHeader title="Your Top Gifts" count={0} />
        <Box
          sx={{
            display: "flex",
            border: "1px solid #e5e5e5",
            borderRadius: "16px",
          }}
        >
          <WidgetEmptyState
            icon={<CardGiftcardOutlined />}
            title="No top gifts yet"
            subtitle="Once your campaigns start redeeming, your most popular gifts will appear here."
          />
        </Box>
      </Box>
    );
  }

  // Each card occupies 75% of the track width so ~1/3 of the next card peeks through
  const CARD_WIDTH_PERCENT = isMobile ? 75 : 20;
  const CARD_GAP = isMobile ? 25 : 15; // px, matches gap between cards

  // Effective card width: layout uses min(percentage, maxWidth), so we must match that for scroll positions
  const getStepSize = (trackWidth: number) => {
    const fromPercent = (trackWidth * CARD_WIDTH_PERCENT) / 100;
    return Math.min(fromPercent, CARD_MAX_WIDTH_PX) + CARD_GAP;
  };

  // Mobile: 1 dot per card. Desktop: 1 dot per page (group of cards)
  const steps = isMobile
    ? gifts.length
    : Math.max(1, Math.ceil(gifts.length / DESKTOP_CARDS_PER_PAGE));

  const scrollToStep = (step: number) => {
    const track = trackRef.current;
    if (!track) return;
    const stepSize = getStepSize(track.clientWidth);
    const cardStep = isMobile ? step : step * DESKTOP_CARDS_PER_PAGE;
    const targetIndex = Math.min(cardStep, gifts.length - 1);
    track.scrollTo({ left: targetIndex * stepSize, behavior: "smooth" });
    setActiveStep(step);
  };

  const handleNext = () => scrollToStep(Math.min(activeStep + 1, steps - 1));
  const handleBack = () => scrollToStep(Math.max(activeStep - 1, 0));

  // Keep activeStep in sync when the user scrolls manually
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const maxScrollLeft = track.scrollWidth - track.clientWidth;
    if (maxScrollLeft <= 0) return;
    if (track.scrollLeft >= maxScrollLeft - 8) {
      setActiveStep(steps - 1);
      return;
    }
    const stepSize = getStepSize(track.clientWidth);
    const cardStep = Math.round(track.scrollLeft / stepSize);
    if (isMobile) {
      setActiveStep(Math.min(cardStep, gifts.length - 1));
    } else {
      const page = Math.min(Math.floor(cardStep / DESKTOP_CARDS_PER_PAGE), steps - 1);
      setActiveStep(Math.max(0, page));
    }
  };

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      <WidgedHeader title="Your Top Gifts" count={gifts.length} />

      {/* Scrollable track – overflow hidden on outer so only the peek gap shows */}
      <Box
        ref={trackRef}
        onScroll={handleScroll}
        tabIndex={0}
        role="region"
        aria-label="Top gifts carousel"
        onKeyDown={(e) => {
          if (e.key === "ArrowRight") handleNext();
          if (e.key === "ArrowLeft") handleBack();
        }}
        sx={{
          display: "flex",
          gap: `${CARD_GAP}px`,
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollBehavior: "smooth",
          // Hide scrollbar cross-browser
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
          outline: "none",
          "&:focus-visible": {
            outline: "2px solid var(--color-dust-ink)",
            outlineOffset: "2px",
          },
          padding: "4px", // small padding so the card doesn't touch the track edge
        }}
      >
        {gifts.map((gift) => (
          <Box
            key={gift.id}
            sx={{
              flex: `0 0 ${CARD_WIDTH_PERCENT}%`,
              scrollSnapAlign: "start",
              maxWidth: "265px",
            }}
          >
            <ProductCard product={gift} />
          </Box>
        ))}
      </Box>

      {/* Dots: mobile = 1 per card, desktop = 1 per page (group of cards) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mt: 2,
          gap: 1,
        }}
      >
        {Array.from({ length: steps }, (_, index) => (
          <Box
            key={isMobile ? `card-${index}` : `page-${index}`}
            role="button"
            tabIndex={0}
            aria-label={
              isMobile ? `Go to gift ${index + 1}` : `Go to page ${index + 1} of ${steps}`
            }
            onClick={() => scrollToStep(index)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                scrollToStep(index);
              }
            }}
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              cursor: "pointer",
              backgroundColor: index === activeStep ? "var(--color-primary-active)" : "grey.300",
              transition: "background-color 0.2s ease",
              "&:hover": {
                backgroundColor: index === activeStep ? "var(--color-primary-active)" : "grey.400",
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default GiftCarousel;
